import { Location } from '@angular/common';
import { Component, EventEmitter, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  NgForm,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import * as roundTo from 'round-to';
import { merge, of as observableOf, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CompanyContactsDialogComponent } from 'src/app/modals/company-contacts-dialog/company-contacts-dialog.component';
import Contact from 'src/app/model/Contact/contact';
import Company, {
  COUNTRY_CHOICES,
  DiscountGroup,
} from 'src/app/model/company/company';
import { COMPANY_PAYMENT_TERMS } from 'src/app/model/company/payment-term';
import { Email } from 'src/app/model/email';
import Address from 'src/app/model/project/address';
import AddressWithType from 'src/app/model/project/address-with-type';
import { Role } from 'src/app/model/role';
import { RoleName } from 'src/app/model/role-name';
import { Telephone } from 'src/app/model/telephone';
import { User } from 'src/app/model/user';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { ModalService } from 'src/app/services/modal.service';
import { RequestService } from 'src/app/services/request.service';
import { SnackService } from 'src/app/services/snack.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { ContactType } from '../../contact/edit-contact/edit-contact.component';
import { FileHelper } from '../../../helpers/file-helper';
import { FileUploader } from 'ng2-file-upload';
import { CollectionHelper } from '../../../helpers/collection-helper';
import { MAX_UPLOAD_SIZE } from '../../../../globals/globals';
import Asset from '../../../model/asset';
import AssetHelper from '../../../helpers/asset-helper';
import { AuthService } from 'src/app/services/auth.service';
import { StringHelper } from 'src/app/helpers/string-helper';

@Component({
  selector: 'edit-company',
  templateUrl: './edit-company.component.html',
  styleUrls: ['./edit-company.component.scss'],
  providers: [FormatPhoneNumber, AssetHelper],
})
export class EditCompanyComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @ViewChild('companyForm')
  public myForm: NgForm;
  public isLoadingObject = false;
  public isInitialised = false;
  public companyForm: UntypedFormGroup;
  private reloadData: EventEmitter<any>;
  protected company: Company = new Company();
  private generalAddress: AddressWithType;
  private invoicingAddress: AddressWithType;
  private deliveryAddress: AddressWithType;
  private invoicingEmail: Email;
  private generalEmail: Email;
  private telephone: Telephone;
  private otherInvoicingAddress: boolean;
  private otherDeliveryAddress: boolean;
  public companyName = '';
  public paymentTermOptions = COMPANY_PAYMENT_TERMS;
  public discountGroupOptions = DISCOUNT_GROUP_OPTIONS;
  public countryOptions = COUNTRY_CHOICES;
  public displayedContacts: Contact[] = [];
  public partnerIntegrationOptions: { value: string; label: string }[] = [];
  private dialogRef: MatDialogRef<CompanyContactsDialogComponent>;
  displayedColumns: string[] = [
    'name',
    'phone',
    'mobile',
    'type',
    'lastLogin',
    'active',
    'email',
    'impersonate',
  ];
  public types = ContactType;

  isImage = FileHelper.isImage;

  public uploader: FileUploader = new FileUploader({
    url: '/api/assets/upload',
  });

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private snackService: SnackService,
    private requestService: RequestService,
    public formatPhoneNumber: FormatPhoneNumber,
    private router: Router,
    public dialog: MatDialog,
    private location: Location,
    private modalService: ModalService,
    private assetHelper: AssetHelper,
    private authService: AuthService
  ) {
    super();
  }

  ngOnInit() {
    this.companyForm = this.fb.group({});
    this.reloadData = new EventEmitter<any>();
    this.loadPartnerReferrers();
    this.initObject();
  }

  private loadPartnerReferrers(): void {
    this.requestService
      .getNormal('/companies/partner-referrers')
      .subscribe((response) => {
        this.partnerIntegrationOptions = response.map((name: string) => ({
          value: name,
          label: name,
        }));
      });
  }

  private buildForm() {
    this.companyForm = this.fb.group({
      name: [this.company.name, [Validators.required]],
      regularName: [this.company.regularName, [Validators.required]],
      dataCorrect: [this.company.dataCorrect, [Validators.nullValidator]],
      newPortalAccess: [
        this.company.newPortalAccess,
        [Validators.nullValidator],
      ],
      generalLine1: [this.generalAddress.address.line1, [Validators.required]],
      generalCity: [this.generalAddress.address.city, [Validators.required]],
      generalPostalCode: [
        this.generalAddress.address.postalCode,
        [Validators.required],
      ],
      generalProvince: [
        this.generalAddress.address.province,
        [Validators.nullValidator],
      ],
      generalCountry: [
        this.generalAddress.address.country,
        [Validators.required],
      ],
      invoicingLine1: [
        this.invoicingAddress.address.line1,
        [Validators.nullValidator],
      ],
      invoicingCity: [
        this.invoicingAddress.address.city,
        [Validators.nullValidator],
      ],
      invoicingPostalCode: [
        this.invoicingAddress.address.postalCode,
        [Validators.nullValidator],
      ],
      invoicingProvince: [
        this.invoicingAddress.address.province,
        [Validators.nullValidator],
      ],
      invoicingCountry: [
        this.invoicingAddress.address.country,
        [Validators.nullValidator],
      ],
      deliveryLine1: [
        this.deliveryAddress.address.line1,
        [Validators.nullValidator],
      ],
      deliveryCity: [
        this.deliveryAddress.address.city,
        [Validators.nullValidator],
      ],
      deliveryPostalCode: [
        this.deliveryAddress.address.postalCode,
        [Validators.nullValidator],
      ],
      deliveryProvince: [
        this.deliveryAddress.address.province,
        [Validators.nullValidator],
      ],
      deliveryCountry: [
        this.deliveryAddress.address.country,
        [Validators.nullValidator],
      ],
      customerSince: [this.company.customerSince, [Validators.nullValidator]],
      vatNumber: [this.company.vat_number, [this.validateVat(BE_VAT)]],
      paymentTerm: [this.company.payment_term, [Validators.required]],
      pendingInvoices: [
        this.company.pendingInvoices,
        [Validators.nullValidator],
      ],
      invoicePerEmail: [
        this.company.invoicePerEmail,
        [Validators.nullValidator],
      ],
      invoicingEmail: [this.invoicingEmail.email, [Validators.nullValidator]],
      immo: [this.company.immo, [Validators.nullValidator]],
      immoscoop: [this.company.immoscoop, [Validators.nullValidator]],
      spotto: [this.company.spotto, [Validators.nullValidator]],
      isRetailer: [this.company.isRetailer, [Validators.nullValidator]],
      activity: [this.company.activity, [Validators.nullValidator]],
      mailNewsletter: [this.company.mailNewsletter, [Validators.nullValidator]],
      openHours: [this.company.openHours, [Validators.nullValidator]],
      info: [this.company.info, [Validators.nullValidator]],
      telephone: [this.telephone.number, [Validators.required]],
      generalEmail: [this.generalEmail.email, [Validators.required]],
      website: [this.company.website, [Validators.nullValidator]],
      discountGroup: [this.company.discountGroup, [Validators.nullValidator]],
      distanceTo: [this.company.distanceTo, [Validators.nullValidator]],
      colorsExtraInfo: [
        this.company.colorsExtraInfo,
        [Validators.nullValidator],
      ],
      externalId: [this.company.externalId, [Validators.nullValidator]],
      partnerIntegrations: [
        this.company.partnerIntegrations || [],
        [Validators.nullValidator],
      ],
      otherInvoicingAddress: [
        this.otherInvoicingAddress,
        [Validators.nullValidator],
      ],
      otherDeliveryAddress: [
        this.otherDeliveryAddress,
        [Validators.nullValidator],
      ],
    });

    this.subscribeDynamicValidators();
    this.isInitialised = true;
  }

  private subscribeDynamicValidators() {
    this.companyForm
      .get('otherInvoicingAddress')
      .valueChanges.subscribe((checked) => {
        if (checked) {
          this.companyForm
            .get('invoicingLine1')
            .setValidators([Validators.required]);
          this.companyForm
            .get('invoicingCity')
            .setValidators([Validators.required]);
          this.companyForm
            .get('invoicingPostalCode')
            .setValidators([Validators.required]);
          this.companyForm
            .get('invoicingCountry')
            .setValidators([Validators.required]);
        } else {
          this.companyForm.get('invoicingLine1').setValidators(null);
          this.companyForm.get('invoicingCity').setValidators(null);
          this.companyForm.get('invoicingPostalCode').setValidators(null);
          this.companyForm.get('invoicingCountry').setValidators(null);
        }
        this.companyForm.get('invoicingLine1').updateValueAndValidity();
        this.companyForm.get('invoicingCity').updateValueAndValidity();
        this.companyForm.get('invoicingPostalCode').updateValueAndValidity();
        this.companyForm.get('invoicingCountry').updateValueAndValidity();
      });
    this.companyForm
      .get('otherDeliveryAddress')
      .valueChanges.subscribe((checked) => {
        if (checked) {
          this.companyForm
            .get('deliveryLine1')
            .setValidators([Validators.required]);
          this.companyForm
            .get('deliveryCity')
            .setValidators([Validators.required]);
          this.companyForm
            .get('deliveryPostalCode')
            .setValidators([Validators.required]);
          this.companyForm
            .get('deliveryCountry')
            .setValidators([Validators.required]);
        } else {
          this.companyForm.get('deliveryLine1').setValidators(null);
          this.companyForm.get('deliveryCity').setValidators(null);
          this.companyForm.get('deliveryPostalCode').setValidators(null);
          this.companyForm.get('deliveryCountry').setValidators(null);
        }
        this.companyForm.get('deliveryLine1').updateValueAndValidity();
        this.companyForm.get('deliveryCity').updateValueAndValidity();
        this.companyForm.get('deliveryPostalCode').updateValueAndValidity();
        this.companyForm.get('deliveryCountry').updateValueAndValidity();
      });
    this.companyForm
      .get('invoicePerEmail')
      .valueChanges.subscribe((checked) => {
        if (checked) {
          this.companyForm
            .get('invoicingEmail')
            .setValidators([Validators.email, Validators.required]);
        } else {
          this.companyForm.get('invoicingEmail').setValidators(null);
        }
        this.companyForm.get('invoicingEmail').updateValueAndValidity();
      });
    if (this.companyForm.get('invoicePerEmail').value) {
      this.companyForm
        .get('invoicingEmail')
        .setValidators([Validators.email, Validators.required]);
      this.companyForm.get('invoicingEmail').updateValueAndValidity();
    }
    this.companyForm.get('regularName').valueChanges.subscribe((name) => {
      this.companyName = name;
    });
  }

  private initObject(): void {
    merge(this.route.params, this.reloadData)
      .pipe(
        switchMap((params) => {
          this.isLoadingObject = true;
          if (params['id'] != null) {
            return this.requestService.get(
              '/companies/' + params['id'],
              null,
              null,
              null,
              null,
              null,
              null
            );
          } else {
            let company = new Company();
            company.newPortalAccess = true;
            let generalAddress = new AddressWithType('primary');
            let invoicingAddress = new AddressWithType('invoicing');
            let deliveryAddress = new AddressWithType('delivery');
            company.addresses = [
              generalAddress,
              invoicingAddress,
              deliveryAddress,
            ];
            let invoicingEmail = new Email();
            invoicingEmail.type = 'invoicing';
            let generalEmail = new Email();
            generalEmail.type = 'primary';
            company.emails = [invoicingEmail, generalEmail];
            let telephone = new Telephone();
            telephone.type = 'phone';
            company.telephones = [telephone];
            return observableOf({ body: company });
          }
        }),
        map((e: any) => {
          // Flip flag to show that loading has finished.
          this.isLoadingObject = false;
          return e;
        }),
        catchError((e) => {
          this.isLoadingObject = false;
          this.router.navigateByUrl('/bedrijven');

          return observableOf([]);
        })
      )
      .subscribe((data) => {
        this.company = data.body as Company;
        this.companyName = this.company.regularName;
        this.displayedContacts =
          this.company.contacts != null
            ? this.company.contacts.filter((c) => !c.archived)
            : [];
        if (this.company.vat_number != null) {
          this.company.vat_number = this.company.vat_number.replace(
            /[\.\s+]/g,
            ''
          );
        }
        this.initAddresses();
        this.initEmails();
        this.initTelephones();
        if (this.company != null) {
          this.buildForm();
        } else {
          this.company = new Company();
          this.buildForm();
        }
      });
  }

  initAddresses() {
    if (this.company.addresses != null) {
      this.company.addresses.forEach((a) => {
        if (a.type != null && a.type === 'primary') {
          this.generalAddress = a;
        }
        if (a.type != null && a.type === 'invoicing') {
          this.invoicingAddress = a;
        }
        if (a.type != null && a.type === 'delivery') {
          this.deliveryAddress = a;
        }
      });
    }
    if (this.company.addresses == null) {
      this.company.addresses = [];
    }
    if (this.generalAddress == null) {
      this.generalAddress = new AddressWithType('primary');
      this.company.addresses.push(this.generalAddress);
    }
    if (this.invoicingAddress == null) {
      this.invoicingAddress = new AddressWithType('invoicing');
      this.company.addresses.push(this.invoicingAddress);
    }
    if (this.deliveryAddress == null) {
      this.deliveryAddress = new AddressWithType('delivery');
      this.company.addresses.push(this.deliveryAddress);
    }
    this.otherInvoicingAddress =
      !this.sameAddress(
        this.generalAddress.address,
        this.invoicingAddress.address
      ) && !this.emptyAddress(this.invoicingAddress.address);
    this.otherDeliveryAddress =
      !this.sameAddress(
        this.generalAddress.address,
        this.deliveryAddress.address
      ) && !this.emptyAddress(this.deliveryAddress.address);
  }

  private sameAddress(a1: Address, a2: Address): boolean {
    return (
      this.trim(a1.line1) === this.trim(a2.line1) &&
      this.trim(a1.city) === this.trim(a2.city) &&
      this.trim(a1.postalCode) === this.trim(a2.postalCode) &&
      this.trim(a1.province) === this.trim(a2.province) &&
      this.trim(a1.country) === this.trim(a2.country)
    );
  }

  private emptyAddress(a: Address): boolean {
    return (
      (a.line1 == null || a.line1 === '') &&
      (a.city == null || a.city === '') &&
      (a.postalCode == null || a.postalCode === '') &&
      (a.province == null || a.province === '') &&
      (a.country == null || a.country === '')
    );
  }

  private trim(s: string): string {
    return s == null ? s : s.trim();
  }

  initEmails() {
    if (this.company.emails != null) {
      this.company.emails.forEach((e) => {
        if (e.type != null && e.type === 'invoicing') {
          this.invoicingEmail = e;
        }
        if (e.type != null && e.type === 'primary') {
          this.generalEmail = e;
        }
      });
    }
    if (this.invoicingEmail == null) {
      this.invoicingEmail = new Email();
      this.invoicingEmail.type = 'invoicing';
      this.company.emails.push(this.invoicingEmail);
    }
    if (this.generalEmail == null) {
      this.generalEmail = new Email();
      this.generalEmail.type = 'primary';
      this.company.emails.push(this.generalEmail);
    }
  }

  initTelephones() {
    if (this.company.telephones != null) {
      this.company.telephones.forEach((t) => {
        if (t.type != null && t.type === 'phone') {
          this.telephone = t;
        }
      });
    }
    if (this.telephone == null) {
      this.telephone = new Telephone();
      this.telephone.type = 'phone';
      this.company.telephones.push(this.telephone);
    }
  }

  save() {
    if (this.companyForm.valid) {
      this.setFormValues();
      if (this.company.pmsColors != null) {
        this.company.pmsColors = this.company.pmsColors.filter(
          (c) => c.id != null || (c.name != null && c.name !== '')
        );
      }
      // NEW
      if (this.company.id === 0 || !this.company.id) {
        this.addSubscription(
          this.requestService.post(this.company, '/companies'),
          (res) => {
            if (res) {
              if (res.success == false) {
                this.snackService.showError(res.message);
              } else {
                this.router.navigateByUrl('/bedrijven');
              }
            }
          },
          (err) => {
            this.handleSaveError(err);
          }
        );
      } // UPDATE
      else {
        this.addSubscription(
          this.requestService.put(this.company, '/companies'),
          (res) => {
            if (res) {
              if (res.success == false) {
                this.snackService.showError(res.message);
              } else {
                this.snackService.showSuccess('Bedrijf succesvol aangepast');
                this.router.navigateByUrl('/bedrijven');
              }
            }
          },
          (err) => {
            this.handleSaveError(err);
          }
        );
      }
    } else {
      this.validateAllFields(this.companyForm);
      this.snackService.showError('Gelieve alle vereiste waarden in te vullen');
    }
  }

  private setFormValues() {
    this.company.name = this.companyForm.get('name').value;
    this.company.regularName = this.companyForm.get('regularName').value;
    this.company.dataCorrect = this.companyForm.get('dataCorrect').value;
    this.company.newPortalAccess =
      this.companyForm.get('newPortalAccess').value;
    this.company.customerSince = this.companyForm.get('customerSince').value;
    this.company.vat_number =
      this.companyForm.get('vatNumber').value == null ||
      this.companyForm.get('vatNumber').value === ''
        ? null
        : this.companyForm.get('vatNumber').value;
    this.company.payment_term = this.companyForm.get('paymentTerm').value;
    this.company.pendingInvoices =
      this.companyForm.get('pendingInvoices').value;
    this.company.invoicePerEmail =
      this.companyForm.get('invoicePerEmail').value;
    this.company.immo = this.companyForm.get('immo').value;
    this.company.immoscoop = this.companyForm.get('immoscoop').value;
    this.company.spotto = this.companyForm.get('spotto').value;
    this.company.isRetailer = this.companyForm.get('isRetailer').value;
    this.company.activity = this.companyForm.get('activity').value;
    this.company.mailNewsletter = this.companyForm.get('mailNewsletter').value;
    this.company.openHours = this.companyForm.get('openHours').value;
    this.company.info = this.companyForm.get('info').value;
    this.company.website = this.companyForm.get('website').value;
    this.company.discountGroup = this.companyForm.get('discountGroup').value;
    this.company.distanceTo =
      this.companyForm.get('distanceTo').value == null ||
      this.companyForm.get('distanceTo').value === ''
        ? null
        : roundTo(Number(this.companyForm.get('distanceTo').value), 2);
    this.company.colorsExtraInfo =
      this.companyForm.get('colorsExtraInfo').value;
    this.company.partnerIntegrations = this.companyForm.get(
      'partnerIntegrations'
    ).value;
    this.setAddresses();
    this.setEmails();
    this.setTelephones();
  }

  private setAddresses() {
    this.generalAddress.address.line1 =
      this.companyForm.get('generalLine1').value;
    this.generalAddress.address.city =
      this.companyForm.get('generalCity').value;
    this.generalAddress.address.postalCode =
      this.companyForm.get('generalPostalCode').value;
    this.generalAddress.address.province =
      this.companyForm.get('generalProvince').value;
    this.generalAddress.address.country =
      this.companyForm.get('generalCountry').value;
    this.invoicingAddress.address.line1 = this.companyForm.get(
      'otherInvoicingAddress'
    ).value
      ? this.companyForm.get('invoicingLine1').value
      : this.companyForm.get('generalLine1').value;
    this.invoicingAddress.address.city = this.companyForm.get(
      'otherInvoicingAddress'
    ).value
      ? this.companyForm.get('invoicingCity').value
      : this.companyForm.get('generalCity').value;
    this.invoicingAddress.address.postalCode = this.companyForm.get(
      'otherInvoicingAddress'
    ).value
      ? this.companyForm.get('invoicingPostalCode').value
      : this.companyForm.get('generalPostalCode').value;
    this.invoicingAddress.address.province = this.companyForm.get(
      'otherInvoicingAddress'
    ).value
      ? this.companyForm.get('invoicingProvince').value
      : this.companyForm.get('generalProvince').value;
    this.invoicingAddress.address.country = this.companyForm.get(
      'otherInvoicingAddress'
    ).value
      ? this.companyForm.get('invoicingCountry').value
      : this.companyForm.get('generalCountry').value;
    this.deliveryAddress.address.line1 = this.companyForm.get(
      'otherDeliveryAddress'
    ).value
      ? this.companyForm.get('deliveryLine1').value
      : this.companyForm.get('generalLine1').value;
    this.deliveryAddress.address.city = this.companyForm.get(
      'otherDeliveryAddress'
    ).value
      ? this.companyForm.get('deliveryCity').value
      : this.companyForm.get('generalCity').value;
    this.deliveryAddress.address.postalCode = this.companyForm.get(
      'otherDeliveryAddress'
    ).value
      ? this.companyForm.get('deliveryPostalCode').value
      : this.companyForm.get('generalPostalCode').value;
    this.deliveryAddress.address.province = this.companyForm.get(
      'otherDeliveryAddress'
    ).value
      ? this.companyForm.get('deliveryProvince').value
      : this.companyForm.get('generalProvince').value;
    this.deliveryAddress.address.country = this.companyForm.get(
      'otherDeliveryAddress'
    ).value
      ? this.companyForm.get('deliveryCountry').value
      : this.companyForm.get('generalCountry').value;
  }

  private setEmails() {
    if (!this.companyForm.get('invoicePerEmail').value) {
      for (let i = 0; i < this.company.emails.length; i++) {
        let email = this.company.emails[i];
        if (
          (email.email == null || email.email === '') &&
          email.type === 'invoicing'
        ) {
          this.company.emails.splice(i, 1);
          break;
        }
      }
    } else {
      this.invoicingEmail.email = this.companyForm.get('invoicingEmail').value;
    }
    this.generalEmail.email = this.companyForm.get('generalEmail').value;
  }

  private setTelephones() {
    this.telephone.number = this.formatPhoneNumber.transform(
      this.companyForm.get('telephone').value
    );
  }

  private handleSaveError(error) {
    let errMsg = error.includes('vat_number')
      ? 'Het opgegeven BTW-nummer is ongeldig'
      : error;

    this.snackService.showError(errMsg);
    return throwError(null);
  }

  comparePaymentTerms(option, select) {
    return (
      option != null &&
      select != null &&
      option.type === select.type &&
      option.days === select.days
    );
  }

  compareText(option, select) {
    return option === select;
  }

  validateAllFields(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        if (control.validator) {
          const validator = control.validator({} as AbstractControl);
          if (
            validator &&
            (validator.required || validator.vatNumberFormat) &&
            (control.value !== '' || control.status === 'INVALID')
          ) {
            control.markAsTouched({ onlySelf: true });
          }
        }
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFields(control);
      }
    });
  }

  validateVat(s: RegExp) {
    return (c: UntypedFormControl) => {
      if (c.value == null || c.value === '') {
        return;
      }
      if (s.test(c.value)) {
        return null;
      } else {
        return {
          vatNumberFormat: true,
        };
      }
    };
  }

  compareCountries(option, select) {
    return option === select;
  }

  urlFriendlyCompanyName() {
    return encodeURIComponent(this.companyName);
  }

  toContact(id) {
    this.router.navigate(['/contacten/edit', id]);
  }

  getContactTelephone(contact: Contact): string {
    if (
      contact == null ||
      contact.telephones == null ||
      contact.telephones.length === 0
    ) {
      return '';
    }
    return this.formatPhoneNumber.transformTelephone(
      contact.telephones.find((t) => t.type === 'phone')
    );
  }

  getContactMobile(contact: Contact): string {
    if (
      contact == null ||
      contact.telephones == null ||
      contact.telephones.length === 0
    ) {
      return '';
    }
    return this.formatPhoneNumber.transformTelephone(
      contact.telephones.find((t) => t.type === 'mobile')
    );
  }

  getTypeLabel(type: string) {
    const foundType = this.types.find((item) => item.value === type);
    return foundType != null ? foundType.label : '';
  }

  openCompanyContactDialog() {
    let config = {
      company: this.company,
    };
    this.dialogRef = this.dialog.open(CompanyContactsDialogComponent, {
      data: config,
    });
    this.addSubscription(this.dialogRef.afterClosed(), () => {
      this.displayedContacts =
        this.company.contacts != null
          ? this.company.contacts.filter((c) => !c.archived)
          : [];
    });
  }

  back() {
    this.location.back();
  }

  invoicePerEmailChange(e: boolean) {
    this.companyForm.patchValue({
      invoicePerEmail: e,
    });
    this.companyForm.updateValueAndValidity();
  }

  login(event: Event, contact: Contact) {
    event.stopPropagation();
    if (contact.contactUserId != null) {
      this.modalService.confirmDialog(
        'Login mail opnieuw versturen',
        'Contact heeft reeds een gebruiker, bent u zeker dat u opnieuw een login mail wil versturen naar deze gebruiker?',
        (b) => {
          if (b) {
            this.addSubscription(
              this.requestService.post(
                {
                  email: contact.emails[0].email,
                  resend: true,
                },
                '/auth/recoverAccount'
              ),
              () => {
                this.snackService.showSuccess('Login mail opnieuw verstuurd');
              }
            );
          }
        }
      );
    } else {
      const user = new User();
      user.firstName = contact.firstName;
      user.lastName = contact.lastName;
      user.email = contact.emails[0].email;
      const tels = contact.telephones.filter((t) => t.type === 'phone');
      user.phone =
        tels != null && tels.length > 0 && tels[0].number != null
          ? contact.telephones[0].number
          : null;
      user.roles = [new Role(RoleName.ROLE_PORTAL_USER)];
      this.requestService.post(user, '/users').subscribe(
        (res) => {
          if (res) {
            if (res.success == false) {
              this.snackService.showError(res.message);
            } else {
              contact.contactUserId = res.id;
              this.snackService.showSuccess('Gebruiker succesvol toegevoegd');
            }
          }
        },
        (err) => {
          this.handleSaveError(err);
        }
      );
    }
  }

  protected getInitials(): string {
    return this.companyName
      ?.split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase())
      .join('');
  }

  async companyLogofileAdded(fileList) {
    let file = this.getAssets(fileList)[0];
    try {
      let base64 = await AssetHelper.extractBase64(file);
      if (this.company.id == null) {
        this.newCompanyLogo(file, base64);
      } else {
        this.existingCompanyLogo(file, base64);
      }
    } catch (e) {
      console.error(e);
      this.snackService.showError(
        'Er is iets mis gegaan bij het opslaan van de bestanden'
      );
      return;
    }
  }

  getAssets(fileList) {
    let assets: File[] = Array.from(fileList);
    if (
      CollectionHelper.isPopulated(assets) &&
      assets.some((a) => a.size > MAX_UPLOAD_SIZE)
    ) {
      this.snackService.showError(
        'Gelieve enkel bestanden kleiner dan 12 MB te selecteren'
      );
      return;
    }

    return assets;
  }

  async newCompanyLogo(file, base64) {
    this.company.logo = await this.assetHelper.uploadFile(file, base64);
    this.snackService.showSuccess('Bestanden succesvol opgeslagen');
  }

  async existingCompanyLogo(file, base64) {
    let editedCompany = await this.changeCompanyLogo(file, base64);
    this.company.logo = editedCompany.logo;
    this.snackService.showSuccess('Bestand succesvol opgeslagen');
  }

  async changeCompanyLogo(file: File, base64: string): Promise<Company> {
    let asset: Asset = {
      name: file.name,
      type: file.type,
      size: file.size,
      base64: base64,
    };
    return await this.requestService
      .post(asset, 'companies/changeCompanyLogo/' + this.company.id)
      .toPromise();
  }

  async uploadImmoboardImage(fileList) {
    let file = this.getAssets(fileList)[0];
    try {
      let base64 = await AssetHelper.extractBase64(file);

      if (this.company.id == null) {
        this.company.immobordImage = await this.assetHelper.uploadFile(
          file,
          base64
        );
        this.snackService.showSuccess('Bestanden succesvol opgeslagen');
      } else {
        let editedCompany = await this.updateCompanyImmobordImage(file, base64);
        this.company.immobordImage = editedCompany.immobordImage;
        this.snackService.showSuccess('Bestand succesvol opgeslagen');
      }
    } catch (e) {
      console.error(e);
      this.snackService.showError(
        'Er is iets mis gegaan bij het opslaan van de bestanden'
      );
      return;
    }
  }

  async uploadKleefboardImage(fileList) {
    let file = this.getAssets(fileList)[0];
    try {
      let base64 = await AssetHelper.extractBase64(file);

      if (this.company.id == null) {
        this.company.kleefbordImage = await this.assetHelper.uploadFile(
          file,
          base64
        );
        this.snackService.showSuccess('Bestanden succesvol opgeslagen');
      } else {
        let editedCompany = await this.updateCompanyKleefbordImage(
          file,
          base64
        );
        this.company.kleefbordImage = editedCompany.kleefbordImage;
        this.snackService.showSuccess('Bestand succesvol opgeslagen');
      }
    } catch (e) {
      console.error(e);
      this.snackService.showError(
        'Er is iets mis gegaan bij het opslaan van de bestanden'
      );
      return;
    }
  }

  async updateCompanyImmobordImage(
    file: File,
    base64: string
  ): Promise<Company> {
    let asset: Asset = {
      name: file.name,
      type: file.type,
      size: file.size,
      base64: base64,
    };
    return await this.requestService
      .post(asset, 'companies/updateCompanyImmobord/' + this.company.id)
      .toPromise();
  }

  async updateCompanyKleefbordImage(
    file: File,
    base64: string
  ): Promise<Company> {
    let asset: Asset = {
      name: file.name,
      type: file.type,
      size: file.size,
      base64: base64,
    };
    return await this.requestService
      .post(asset, 'companies/updateCompanyKleefbord/' + this.company.id)
      .toPromise();
  }

  impersonateContact(contact: Contact): void {
    if (contact != null && contact.contactUserId != null) {
      const userName = StringHelper.join(
        [contact.firstName, contact.lastName],
        ' '
      );
      this.modalService.confirmDialog(
        `${userName} nabootsen`,
        `Bent u zeker dat u ${userName} wilt nabootsen?`,
        (b: boolean) => {
          if (b) {
            this.authService.impersonateUser({ id: contact.contactUserId });
          }
        }
      );
    }
  }
}

const DISCOUNT_GROUP_OPTIONS = Object.values(DiscountGroup);
const BE_VAT = /^BE[0-9]{10}$/;
